import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Container, Paper, Button, Box, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControlLabel, Switch, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import { Add, Edit, Delete, Lock } from '@mui/icons-material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { preOpChecklistItemService } from '../../services/api';
import TopBar from '../../components/TopBar';

const emptyForm = { name: '', description: '', isActive: true, sortOrder: 0 };

const PreOpChecklistManagementPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await preOpChecklistItemService.getAll();
      setItems(res.data);
    } catch {
      setError('Failed to load checklist items');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditingItem(null);
    setForm(emptyForm);
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingItem(row);
    setForm({ name: row.name, description: row.description || '', isActive: row.isActive, sortOrder: row.sortOrder });
    setError('');
    setDialogOpen(true);
  };

  const handleClose = () => { setDialogOpen(false); setEditingItem(null); setError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Item name is required.'); return; }
    setSaving(true);
    try {
      if (editingItem) {
        await preOpChecklistItemService.update(editingItem.id, form);
      } else {
        await preOpChecklistItemService.create(form);
      }
      handleClose();
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (row.isSystem) { alert('System items cannot be deleted. Deactivate them instead.'); return; }
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await preOpChecklistItemService.delete(row.id);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Pre-Op Checklist Items" onBack={() => navigate('/dashboard')} />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Define the pre-operative checklist items available for selection. Assign them to surgery types in <strong>Surgery Type Mapping</strong>. System items (🔒) cannot be deleted — only deactivated.
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openAdd} sx={{ ml: 2, whiteSpace: 'nowrap' }}>
            Add Item
          </Button>
        </Paper>

        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
        ) : (
          <Paper elevation={3} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell width={60}><strong>Order</strong></TableCell>
                  <TableCell><strong>Item Name</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell width={100}><strong>Status</strong></TableCell>
                  <TableCell width={130}><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No checklist items found.
                    </TableCell>
                  </TableRow>
                )}
                {items.map(row => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        {row.sortOrder}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography fontWeight={600}>{row.name}</Typography>
                        {row.isSystem && (
                          <Chip size="small" label="System" icon={<Lock sx={{ fontSize: '14px !important' }} />}
                            color="default" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{row.description || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      {row.isActive
                        ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
                            <CheckCircle fontSize="small" /><Typography variant="caption">Active</Typography>
                          </Box>
                        : <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.disabled' }}>
                            <Cancel fontSize="small" /><Typography variant="caption">Inactive</Typography>
                          </Box>
                      }
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => openEdit(row)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={row.isSystem ? 'System item — cannot delete' : 'Delete'}>
                        <span>
                          <IconButton size="small" color="error" onClick={() => handleDelete(row)} disabled={row.isSystem}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Container>

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Checklist Item' : 'Add Checklist Item'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
            <TextField
              fullWidth required label="Item Name" sx={{ mb: 2 }}
              placeholder="e.g. Allergy Assessment, Blood Pressure Check"
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            />
            <TextField
              fullWidth label="Description" multiline rows={2} sx={{ mb: 2 }}
              placeholder="Brief description of what this item involves"
              value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            />
            <TextField
              fullWidth label="Sort Order" type="number" sx={{ mb: 2 }}
              helperText="Lower numbers appear first in the checklist"
              value={form.sortOrder}
              onChange={e => setForm(p => ({ ...p, sortOrder: parseInt(e.target.value, 10) || 0 }))}
              inputProps={{ min: 0 }}
            />
            <FormControlLabel
              control={<Switch checked={form.isActive} onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))} color="success" />}
              label="Active"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Saving…' : (editingItem ? 'Update' : 'Add')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default PreOpChecklistManagementPage;
